import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { DomSanitizer } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { OrdersService } from '@core/services/orders/orders.service';
import { isPeruHoliday } from 'src/assets/data/peru-holidays';
import {
  DELIVERY_DATE_FORMATS,
  DeliveryDateAdapter,
} from './delivery-date-adapter';
import { DeliveryDateCalendarHeaderComponent } from './delivery-date-calendar-header.component';

export interface ChangeDeliveryDateData {
  /** Fecha preferente de entrega vigente del pedido. */
  deliveryDate: Date;
  /**
   * Fecha guía del pedido, sin las ediciones que haya hecho el cliente. Ancla
   * el rango seleccionable, así que reprogramar no lo corre: de otro modo se
   * podría avanzar indefinidamente editando una y otra vez.
   */
  minDate: Date;
  /** Documento SAP del pedido que se va a reprogramar. */
  sapOrderDocument: string;
}

/** Resultado con el que cierra el modal tras intentar guardar la fecha. */
export interface ChangeDeliveryDateResult {
  deliveryDate: Date;
  saved: boolean;
}

/** Días hábiles habilitados a partir de la fecha preferente de entrega. */
const SELECTABLE_BUSINESS_DAYS = 8;

/** Ajusta el bloqueo de scroll del CDK solo mientras este modal está abierto. */
const SCROLL_BLOCK_CLASS = 'change-delivery-date-open';

@Component({
  selector: 'app-change-delivery-date-modal',
  templateUrl: './change-delivery-date-modal.component.html',
  styleUrls: ['./change-delivery-date-modal.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: DeliveryDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DELIVERY_DATE_FORMATS },
  ],
})
export class ChangeDeliveryDateModalComponent implements OnInit {
  readonly calendarHeader = DeliveryDateCalendarHeaderComponent;

  selectedDate!: Date;

  /** El modal se mantiene abierto mientras el servicio responde. */
  isSaving: boolean = false;

  /** Días habilitados en el calendario; el resto se muestra deshabilitado. */
  private availableDates: Date[] = [];

  constructor(
    public readonly dialogRef: MatDialogRef<ChangeDeliveryDateModalComponent>,
    private readonly _ordersService: OrdersService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public readonly data: ChangeDeliveryDateData
  ) {
    this.matIconRegistry.addSvgIcon(
      `datepicker_icon`,
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/svg/date-picker-icon.svg'
      )
    );
  }

  /**
   * Abre el modal marcando el documento antes de que el CDK bloquee el scroll.
   * Usar siempre este método en lugar de `dialog.open` directo.
   */
  static open(
    dialog: MatDialog,
    data: ChangeDeliveryDateData
  ): MatDialogRef<ChangeDeliveryDateModalComponent, ChangeDeliveryDateResult> {
    document.documentElement.classList.add(SCROLL_BLOCK_CLASS);

    const dialogRef = dialog.open<
      ChangeDeliveryDateModalComponent,
      ChangeDeliveryDateData,
      ChangeDeliveryDateResult
    >(ChangeDeliveryDateModalComponent, {
      panelClass: 'change-delivery-date-dialog',
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() =>
        document.documentElement.classList.remove(SCROLL_BLOCK_CLASS)
      );

    return dialogRef;
  }

  ngOnInit(): void {
    this.availableDates = this.buildAvailableDates(this.data?.minDate);
    this.selectedDate = this.isAvailable(this.data?.deliveryDate)
      ? this.startOfDay(this.data.deliveryDate)
      : this.availableDates[0];

    this.alignDialogBelowHeader();
  }

  /**
   * El modal no va centrado en la pantalla sino hacia arriba, justo debajo del
   * header, cuya altura cambia entre desktop y responsive. Se usa su altura y
   * no su posición porque el header se oculta al hacer scroll.
   */
  private alignDialogBelowHeader(): void {
    const header = document.querySelector<HTMLElement>('header');

    document.documentElement.style.setProperty(
      '--change-delivery-date-dialog-top',
      `${header?.offsetHeight ?? 0}px`
    );
  }

  get minSelectableDate(): Date {
    return this.availableDates[0];
  }

  get maxSelectableDate(): Date {
    return this.availableDates[this.availableDates.length - 1];
  }

  readonly dateFilter = (date: Date | null): boolean => this.isAvailable(date);

  /**
   * Guarda la fecha sin cerrar el modal: recién al responder el servicio se
   * cierra, para que el cliente vea el estado de carga y no un salto en seco.
   */
  changeDate(): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    this._ordersService
      .orderEdit({
        sapOrderDocument: this.data?.sapOrderDocument,
        preferredDeliveryDate: this.formatApiDate(this.selectedDate),
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.closeWithResult(true),
        error: () => this.closeWithResult(false),
      });
  }

  closeDialog(): void {
    if (this.isSaving) {
      return;
    }

    this.dialogRef.close();
  }

  private closeWithResult(saved: boolean): void {
    this.isSaving = false;

    const result: ChangeDeliveryDateResult = {
      deliveryDate: this.selectedDate,
      saved,
    };

    this.dialogRef.close(result);
  }

  // El servicio espera la fecha en formato YYYY-MM-DD
  private formatApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
  }

  /**
   * Recorre el calendario desde la fecha guía del pedido hasta reunir los días
   * hábiles habilitados.
   */
  private buildAvailableDates(minDate: Date): Date[] {
    const dates: Date[] = [];
    const day = this.startOfDay(minDate ?? new Date());

    while (dates.length < SELECTABLE_BUSINESS_DAYS) {
      if (this.isBusinessDay(day)) {
        dates.push(new Date(day));
      }
      day.setDate(day.getDate() + 1);
    }

    return dates;
  }

  private isAvailable(date: Date | null): boolean {
    if (!date) return false;

    const time = this.startOfDay(date).getTime();
    return this.availableDates.some((available) => available.getTime() === time);
  }

  /** El sábado sí es día de atención; solo quedan fuera domingos y feriados. */
  private isBusinessDay(date: Date): boolean {
    if (date.getDay() === 0) return false;

    return !isPeruHoliday(date);
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }
}
